<template>
  <v-card class="system-card" elevation="4">
    <v-card-title class="d-flex align-center">
      <div>
        <h2 class="section-title">Systeminformationen</h2>
        <div class="subtitle">{{ system.host }}</div>
      </div>
      <v-spacer></v-spacer>
      <v-chip color="primary" small class="refresh-chip" @click="$emit('refresh')">
        <v-icon left small>mdi-refresh</v-icon>
        Aktualisieren
      </v-chip>
    </v-card-title>
    
    <v-card-text>
      <v-row>
        <!-- Systemmetriken -->
        <v-col cols="12" md="4" v-for="(metric, index) in systemMetrics" :key="index">
          <v-card class="metric-card" :color="metric.color" flat>
            <div class="d-flex align-center">
              <div class="metric-gauge">
                <v-progress-circular
                  :rotate="270"
                  :size="90"
                  :width="12"
                  :value="metric.value"
                  :color="metric.gaugeColor"
                  class="metric-progress"
                >
                  <span class="metric-value">{{ Math.round(metric.value) }}%</span>
                </v-progress-circular>
              </div>
              <div class="metric-info ml-4">
                <h3 class="metric-title">{{ metric.title }}</h3>
                <div class="metric-details">{{ metric.details }}</div>
              </div>
            </div>
          </v-card>
        </v-col>
      </v-row>
      
      <v-row class="mt-4">
        <!-- CPU Temperatur -->
        <v-col cols="12" md="6">
          <v-card class="temperature-card" elevation="0">
            <div class="d-flex align-center">
              <v-icon size="40" :color="getCpuTempColor" class="mr-4">mdi-thermometer</v-icon>
              <div>
                <h3 class="metric-title">CPU-Temperatur</h3>
                <div class="temperature-container">
                  <div class="temperature-bar" :style="{ width: getTempPercentage + '%', backgroundColor: getTempGradientColor }"></div>
                  <div class="temperature-value text-h4 font-weight-bold">{{ system.cpuTemp }}°C</div>
                </div>
              </div>
            </div>
          </v-card>
        </v-col>
        
        <!-- Server Load -->
        <v-col cols="12" md="6">
          <v-card class="load-card" elevation="0">
            <h3 class="metric-title mb-2">Server Load (12 Kerne)</h3>
            
            <!-- Server Load Chart -->
            <div class="server-load-wrapper mb-2">
              <v-sparkline
                :value="system.load"
                smooth
                :gradient="['#4CAF50', '#FFA726', '#FF5252']"
                gradient-direction="top"
                fill
                height="80"
                padding="6"
                :line-width="2"
              ></v-sparkline>
              
              <!-- Reference lines -->
              <div class="reference-container">
                <div class="reference-line" style="top: 20%;">
                  <span class="reference-label">12 Kerne</span>
                </div>
                <div class="reference-line" style="top: 50%;">
                  <span class="reference-label-left">6 Kerne</span>
                </div>
              </div>
            </div>
            
            <!-- Load statistics -->
            <div class="d-flex justify-space-between load-labels mb-2">
              <div class="load-label">1 min: <b>{{ system.load[0].toFixed(2) }}</b></div>
              <div class="load-label">5 min: <b>{{ system.load[1].toFixed(2) }}</b></div>
              <div class="load-label">15 min: <b>{{ system.load[2].toFixed(2) }}</b></div>
            </div>
            
            <!-- Load status indicator -->
            <div class="load-status-bar">
              <div class="load-status-progress" :style="{ width: getCurrentLoadPercent + '%', backgroundColor: getLoadStatusColor }"></div>
            </div>
            <div class="d-flex justify-space-between mt-1">
              <v-chip small :color="getLoadChipColor" dark class="my-1">
                {{ getLoadStatus }}
              </v-chip>
              <span class="load-percent align-self-center">{{ getCurrentLoadPercent }}% Auslastung</span>
            </div>
          </v-card>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
export default {
  name: 'SystemInfo',
  
  props: {
    system: {
      type: Object,
      required: true
    }
  },
  
  computed: {
    systemMetrics() {
      if (!this.system) return [];
      
      return [
        {
          title: 'CPU Auslastung',
          value: this.system.cpuUsage || 0,
          details: `${Math.round(this.system.cpuUsage || 0)}% verwendet`,
          color: 'rgba(33, 150, 243, 0.1)',
          gaugeColor: this.getCpuColor
        },
        {
          title: 'Arbeitsspeicher',
          value: this.system.memUsage || 0,
          details: `${this.system.memUsage}% verwendet`,
          color: 'rgba(156, 39, 176, 0.1)',
          gaugeColor: this.getMemoryColor
        },
        {
          title: 'Festplatte',
          value: this.system.diskUsage || 0,
          details: `${this.system.diskUsage}% belegt`,
          color: 'rgba(255, 152, 0, 0.1)',
          gaugeColor: this.getDiskColor
        }
      ];
    },
    
    getCpuColor() {
      const usage = this.system?.cpuUsage || 0;
      if (usage < 50) return 'success';
      if (usage < 80) return 'warning';
      return 'error';
    },
    
    getMemoryColor() {
      const usage = this.system?.memoryUsage || 0;
      if (usage < 60) return 'success';
      if (usage < 85) return 'warning';
      return 'error';
    },
    
    getDiskColor() {
      const usage = this.system?.diskUsage || 0;
      if (usage < 70) return 'success';
      if (usage < 90) return 'warning';
      return 'error';
    },
    
    getCpuTempColor() {
      const temp = this.system?.cpuTemp || 0;
      if (temp < 60) return 'success';
      if (temp < 75) return 'warning';
      return 'error';
    },
    
    getTempPercentage() {
      const temp = this.system?.cpuTemp || 0;
      return Math.min((temp / 100) * 100, 100);
    },
    
    getTempGradientColor() {
      const temp = this.system?.cpuTemp || 0;
      if (temp < 60) return '#4CAF50';
      if (temp < 75) return '#FFA726';
      return '#FF5252';
    },
    
    getCurrentLoadPercent() {
      if (!this.system?.load) return 0;
      const currentLoad = this.system.load[0];
      const maxLoad = 12; // 12 cores
      return Math.min(Math.round((currentLoad / maxLoad) * 100), 100);
    },
    
    getLoadStatus() {
      const percent = this.getCurrentLoadPercent;
      if (percent < 30) return 'Niedrig';
      if (percent < 60) return 'Normal';
      if (percent < 80) return 'Hoch';
      return 'Kritisch';
    },
    
    getLoadChipColor() {
      const percent = this.getCurrentLoadPercent;
      if (percent < 30) return 'success';
      if (percent < 60) return 'info';
      if (percent < 80) return 'warning';
      return 'error';
    },
    
    getLoadStatusColor() {
      const percent = this.getCurrentLoadPercent;
      if (percent < 30) return '#4CAF50';
      if (percent < 60) return '#2196F3';
      if (percent < 80) return '#FFA726';
      return '#FF5252';
    }
  },
  
  methods: {
    formatBytes(bytes) {
      if (!bytes) return '0 B';
      const k = 1024;
      const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    }
  }
};
</script>

<style scoped>
.section-title {
  font-size: 1.2rem;
  font-weight: 500;
}

.system-card {
  background: linear-gradient(135deg, rgba(30, 30, 30, 0.95) 0%, rgba(20, 20, 20, 0.95) 100%);
}

.subtitle {
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.6);
}

.metric-card {
  padding: 16px;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.metric-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

.metric-gauge {
  position: relative;
}

.metric-value {
  font-size: 1.2rem;
  font-weight: bold;
}

.metric-title {
  font-size: 1rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
}

.metric-details {
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.7);
  margin-top: 4px;
}

.temperature-card {
  padding: 16px;
  background: rgba(255, 255, 255, 0.05);
}

.temperature-container {
  position: relative;
  height: 8px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  margin: 8px 0;
}

.temperature-bar {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  border-radius: 4px;
  transition: all 0.3s ease;
}

.temperature-value {
  margin-top: 8px;
}

.load-card {
  padding: 16px;
  background: rgba(255, 255, 255, 0.05);
}

.server-load-wrapper {
  position: relative;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 4px;
}

.reference-container {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
}

.reference-line {
  position: absolute;
  left: 0;
  right: 0;
  height: 1px;
  background: rgba(255, 255, 255, 0.1);
  border-top: 1px dashed rgba(255, 255, 255, 0.2);
}

.reference-label,
.reference-label-left {
  position: absolute;
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.4);
  top: -8px;
  background: rgba(0, 0, 0, 0.5);
  padding: 0 4px;
}

.reference-label {
  right: 4px;
}

.reference-label-left {
  left: 4px;
}

.load-labels {
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.7);
}

.load-status-bar {
  height: 4px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 2px;
  overflow: hidden;
}

.load-status-progress {
  height: 100%;
  transition: all 0.3s ease;
}

.load-percent {
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.7);
}

.refresh-chip {
  cursor: pointer;
  transition: all 0.2s ease;
}

.refresh-chip:hover {
  transform: scale(1.05);
}
</style>